import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from '../../header/header.component';
import { SuperadminService } from 'src/app/services/superadmin.service';
import { BsModalService, BsModalRef, ModalModule } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    ReactiveFormsModule,
    ModalModule,
    TooltipModule,
    NgSelectModule
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  providers: [BsModalService]
})
export class DashboardComponent implements OnInit {
  packages: any[] = []; // Array to hold packages data
  tests: any[] = []; // Array to hold tests data
  mappings: any[] = []; // Array to hold mappings data
  companies: any[] = []; // Array to hold companies data
  createPackageForm!: FormGroup; // Form for creating a package
  createTestForm!: FormGroup; // Form for creating a test
  createMappingForm!: FormGroup; // Form for creating a mapping
  modalRef!: BsModalRef; // Reference to the modal
  headerMessage: string = ''; // Message for modal header
  bodyMessage: string = ''; // Message for modal body
  activeSection: string = 'tests'; // Active section to show

  @ViewChild('successTemplate') successTemplate!: TemplateRef<any>; // Reference to the success modal template
  @ViewChild('failureTemplate') failureTemplate!: TemplateRef<any>; // Reference to the failure modal template

  constructor(
    private superadminService: SuperadminService,
    private fb: FormBuilder,
    private modalService: BsModalService
  ) {}

  ngOnInit(): void {
    this.fetchPackages();
    this.fetchTests();
    this.fetchMappings();
    this.initializeForms();
  }

  // Fetch packages data
  fetchPackages(): void {
    this.superadminService.getPackages().subscribe(
      (response) => {
        this.packages = response;
      },
      (error) => {
        console.error('Error fetching packages:', error);
      }
    );
  }

  // Fetch tests data
  fetchTests(): void {
    this.superadminService.getTests().subscribe(
      (response) => {
        this.tests = response;
      },
      (error) => {
        console.error('Error fetching tests:', error);
      }
    );
  }

  // Fetch mappings data
  fetchMappings(): void {
    this.superadminService.getMappings().subscribe(
      (response) => {
        this.mappings = response?.data;
        this.companies = response?.data;

      },
      (error) => {
        console.error('Error fetching mappings:', error);
      }
    );
  }

  // Initialize forms
  initializeForms(): void {
    this.createPackageForm = this.fb.group({
      packageName: ['', Validators.required],
      selectedTests: [[], Validators.required]
    });

    this.createTestForm = this.fb.group({
      testName: ['', Validators.required],
      description: ['', Validators.required]
    });

    this.createMappingForm = this.fb.group({
      company: ['', Validators.required],
      selectedPackages: [[], Validators.required]
    });
  }

  // Show the selected section
  showSection(section: string): void {
    this.activeSection = section;
  }

  // Open create package modal
  openCreatePackageModal(template: any): void {
    this.createPackageForm.reset();
    this.modalRef = this.modalService.show(template);
  }

  // Open create test modal
  openCreateTestModal(template: any): void {
    this.createTestForm.reset();
    this.modalRef = this.modalService.show(template);
  }

  // Open create mapping modal
  openCreateMappingModal(template: any): void {
    this.createMappingForm.reset();
    this.modalRef = this.modalService.show(template);
  }

  // Submit create package form
  onSubmitPackage(): void {
    if (this.createPackageForm.valid) {
      const packageData = {
        packageName: this.createPackageForm.value.packageName,
        testIds: this.createPackageForm.value.selectedTests
      };
      this.superadminService.createPackage(packageData).subscribe(
        (response) => {
          console.log('Package created successfully:', response);
          this.headerMessage = 'Success';
          this.bodyMessage = 'Package created successfully.';
          this.modalRef.hide(); // Close the create package modal
          this.modalRef = this.modalService.show(this.successTemplate); // Show success modal
          this.fetchPackages(); // Refresh packages list
        },
        (error) => {
          console.error('Error creating package:', error);
          this.headerMessage = 'Error';
          this.bodyMessage = 'Failed to create package.';
          this.modalRef.hide(); // Close the create package modal
          this.modalRef = this.modalService.show(this.failureTemplate); // Show failure modal
        }
      );
    }
  }

  // Submit create test form
  onSubmitTest(): void {
    if (this.createTestForm.valid) {
      const testData = {
        testName: this.createTestForm.value.testName,
        description: this.createTestForm.value.description
      };
      this.superadminService.createTest(testData).subscribe(
        (response) => {
          console.log('Test created successfully:', response);
          this.headerMessage = 'Success';
          this.bodyMessage = 'Test created successfully.';
          this.modalRef.hide(); // Close the create test modal
          this.modalRef = this.modalService.show(this.successTemplate); // Show success modal
          this.fetchTests(); // Refresh tests list
        },
        (error) => {
          console.error('Error creating test:', error);
          this.headerMessage = 'Error';
          this.bodyMessage = 'Failed to create test.';
          this.modalRef.hide(); // Close the create test modal
          this.modalRef = this.modalService.show(this.failureTemplate); // Show failure modal
        }
      );
    }
  }

  // Submit create mapping form
  onSubmitMapping(): void {
    if (this.createMappingForm.valid) {
      const mappingData = {
        organisationName: this.createMappingForm.value.company,
        testPackageIds: this.createMappingForm.value.selectedPackages
      };
      this.superadminService.createMapping(mappingData).subscribe(
        (response) => {
          console.log('Mapping created successfully:', response);
          this.headerMessage = 'Success';
          this.bodyMessage = 'Mapping created successfully.';
          this.modalRef.hide(); // Close the create mapping modal
          this.modalRef = this.modalService.show(this.successTemplate); // Show success modal
          this.fetchMappings(); // Refresh mappings list
        },
        (error) => {
          console.error('Error creating mapping:', error);
          this.headerMessage = 'Error';
          this.bodyMessage = 'Failed to create mapping.';
          this.modalRef.hide(); // Close the create mapping modal
          this.modalRef = this.modalService.show(this.failureTemplate); // Show failure modal
        }
      );
    }
  }
}